import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProjects } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { StatusDot } from '@/components/ui/status-dot';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Search, X } from 'lucide-react';

/* Below 640px the table collapses: header hidden, each cell stacked with its micro label. */
const ROW = 'block cursor-pointer py-2 sm:table-row sm:py-0';
const CELL = 'flex h-auto items-baseline justify-between gap-4 px-0 py-1 before:text-micro before:uppercase before:text-ink-subtle before:content-[attr(data-label)] sm:table-cell sm:h-11 sm:px-3 sm:py-0 sm:before:content-none';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const navigate = useNavigate();

  useEffect(() => { getProjects().then(r => { setProjects(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const filtered = projects.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(p.name || '').toLowerCase().includes(q) && !(p.client_name || '').toLowerCase().includes(q)) return false;
    }
    if (dateFrom && new Date(p.created_at) < new Date(dateFrom)) return false;
    if (dateTo) {
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      if (new Date(p.created_at) > end) return false;
    }
    return true;
  });

  const hasFilters = search || dateFrom || dateTo || statusFilter !== 'all';
  const clearFilters = () => { setSearch(''); setDateFrom(''); setDateTo(''); setStatusFilter('all'); };

  const open = (p) => navigate(`/projects/${p.id}`);

  return (
    <div data-testid="projects-page">
      <PageHeader
        title="Projects"
        description={`${filtered.length} project${filtered.length !== 1 ? 's' : ''}${hasFilters ? ` of ${projects.length}` : ''}`}
      />

      <div className="space-y-4">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
            <TabsTrigger value="paused">Paused</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by project name or client"
              className="pl-9"
              data-testid="search-projects"
            />
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[168px] tabular-nums" data-testid="date-from" />
            <span className="text-small text-ink-subtle">to</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[168px] tabular-nums" data-testid="date-to" />
            {hasFilters && (
              <Button variant="ghost" size="icon" onClick={clearFilters} aria-label="Clear filters" data-testid="clear-filters">
                <X strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 space-y-px border-t border-line pt-px">
          {[1, 2, 3].map(i => <div key={i} className="h-11 animate-pulse bg-wash" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          className="mt-6"
          title={hasFilters ? 'No projects match your filters' : 'No projects yet'}
          description={hasFilters
            ? 'Try a different search term or date range.'
            : 'Projects are created automatically when a proposal is accepted.'}
          action={hasFilters ? <Button variant="outline" onClick={clearFilters}>Clear filters</Button> : undefined}
        />
      ) : (
        <div className="mt-6">
          <Table>
            <TableHeader className="hidden sm:table-header-group">
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Files</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(p => (
                <TableRow
                  key={p.id}
                  className={ROW}
                  role="link"
                  tabIndex={0}
                  onClick={() => open(p)}
                  onKeyDown={e => { if (e.key === 'Enter') open(p); }}
                  data-testid={`project-card-${p.id}`}
                >
                  <TableCell data-label="Project" className={`${CELL} text-ink`}>{p.name}</TableCell>
                  <TableCell data-label="Client" className={`${CELL} text-ink-muted`}>{p.client_name}</TableCell>
                  <TableCell data-label="Created" className={`${CELL} text-ink-muted`}>
                    {new Date(p.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell data-label="Status" className={CELL}><StatusDot status={p.status} /></TableCell>
                  <TableCell data-label="Files" className={`${CELL} text-ink-muted sm:text-right`}>{p.deliverable_count || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
